/**
 * "About [School]" heritage section for school pages.
 *
 * What: Renders school heritage blurb, short fact chips, and longer fact rows (vision/mission).
 * Why: Adds genuinely unique content per school to address Google thin-content issues.
 * When: Update when heritage data structure or styling changes.
 */
import React from 'react';
import { Box, Chip, Paper, Stack, Typography } from '@mui/material';

import LandingSectionShell from '@/components/landing/LandingSectionShell';
import { colors, radii, shadows } from '@/components/landing/landingTokens';
import type { SchoolAboutContent } from '@/content/schoolPageContent';

/**
 * Anchor id, for the contents nav.
 *
 * Not a scroll-reveal key: the school run paints on arrival rather than
 * animating in — see the note above `support` in `sectionRevealMap`.
 */
export const SCHOOL_ABOUT_SECTION_ID = 'school-about';

interface SchoolAboutSectionProps {
  content: SchoolAboutContent;
}

/** Facts shorter than this render as chips; longer ones render as label + text rows. */
const CHIP_VALUE_MAX_LENGTH = 60;

function SchoolAboutSection({ content }: SchoolAboutSectionProps) {
  const { heading, body, facts } = content;
  const titleId = 'school-about-title';

  if (!body && facts.length === 0) return null;

  const chipFacts = facts.filter(f => f.value.length <= CHIP_VALUE_MAX_LENGTH);
  const longFacts = facts.filter(f => f.value.length > CHIP_VALUE_MAX_LENGTH);

  return (
    <LandingSectionShell
      id={SCHOOL_ABOUT_SECTION_ID}
      ariaLabelledby={titleId}
      // Spacing between these sections comes from the page-level stack.
      mb={0}
      sx={{ width: '100%' }}
    >
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${colors.border}`,
          borderRadius: radii.md,
          backgroundColor: colors.backgroundAlt,
          boxShadow: shadows.card,
          p: { xs: 2.5, sm: 3, md: 4 },
        }}
      >
        <Stack spacing={{ xs: 2, sm: 2.5 }}>
          <Typography
            id={titleId}
            component="h2"
            sx={{
              fontSize: { xs: '1.375rem', sm: '1.5rem', md: '1.625rem' },
              fontWeight: 800,
              color: colors.ink,
              lineHeight: 1.25,
              letterSpacing: '-0.02em',
            }}
          >
            {heading}
          </Typography>

          {body && (
            <Typography
              sx={{
                fontSize: { xs: '0.9375rem', sm: '1rem' },
                color: colors.inkMuted,
                lineHeight: 1.65,
              }}
            >
              {body}
            </Typography>
          )}

          {chipFacts.length > 0 && (
            <Stack
              direction="row"
              spacing={1}
              sx={{ flexWrap: 'wrap', gap: 1 }}
            >
              {chipFacts.map(fact => (
                <Chip
                  key={fact.label}
                  label={`${fact.label}: ${fact.value}`}
                  size="small"
                  sx={{
                    backgroundColor: colors.washAmber,
                    color: colors.ink,
                    fontWeight: 600,
                    fontSize: '0.8125rem',
                    borderRadius: radii.sm,
                    border: `1px solid ${colors.accentOrange}25`,
                  }}
                />
              ))}
            </Stack>
          )}

          {longFacts.length > 0 && (
            <Stack spacing={1.5}>
              {longFacts.map(fact => (
                <Box key={fact.label}>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      fontWeight: 700,
                      color: colors.ink,
                    }}
                  >
                    {fact.label}:
                  </Typography>{' '}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: { xs: '0.875rem', sm: '0.9375rem' },
                      color: colors.inkMuted,
                      lineHeight: 1.6,
                    }}
                  >
                    {fact.value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </Stack>
      </Paper>
    </LandingSectionShell>
  );
}

export default SchoolAboutSection;
